<template>
  <div class="customers-layout">
    <!-- Sidebar -->
    <Sidebar :isAgent="isAgent" />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <div class="customers-container">
          <!-- Page Header -->
          <div v-if="!isAgent" class="page-header-actions">
            <div class="action-buttons-group">
              <button @click="showUploadModal = true" class="btn-import">
                <svg class="btn-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9,16V10H5L12,3L19,10H15V16H9M5,20V18H19V20H5Z"/>
                </svg>
                Import Customers
              </button>
              <button @click="showExportModal = true" class="btn-export">
                <svg class="btn-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M14,2L20,8V20A2,2 0 0,1 18,22H6A2,2 0 0,1 4,20V4A2,2 0 0,1 6,2H14M18,20V9H13V4H6V20H18Z M12,12L16,16H13V19H11V16H8L12,12Z"/>
                </svg>
                Export Customers
              </button>
              <button @click="showAddModal = true" class="btn-primary">
                <svg class="btn-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2c5.514 0 10 4.486 10 10s-4.486 10-10 10-10-4.486-10-10 4.486-10 10-10zm0 1c-4.962 0-9 4.038-9 9s4.038 9 9 9 9-4.038 9-9-4.038-9-9-9zm-1 4h2v4h4v2h-4v4h-2v-4h-4v-2h4v-4z"/>
                </svg>
                Add Customer
              </button>
            </div>
          </div>

          <!-- Filters -->
          <div class="filters-section">
            <div class="filters-row">
              <div class="filter-group">
                <label class="filter-label">Search by Name</label>
                <input
                  v-model="filters.search_name"
                  @input="debouncedSearch"
                  type="text"
                  placeholder="Enter customer name..."
                  class="filter-input"
                />
              </div>
              <div class="filter-group">
                <label class="filter-label">Phone Number</label>
                <input
                  v-model="filters.phone_number"
                  @input="debouncedSearch"
                  type="text"
                  placeholder="Enter phone number..."
                  class="filter-input"
                />
              </div>
              <div class="filter-group">
                <label class="filter-label">Category</label>
                <select
                  v-model="filters.category_id"
                  @change="loadCustomers"
                  class="filter-select"
                >
                  <option value="">All Categories</option>
                  <option
                    v-for="category in categories"
                    :key="category.id"
                    :value="category.id"
                  >
                    {{ category.name }}
                  </option>
                </select>
              </div>
              <div class="filter-group">
                <label class="filter-label">VIP Only</label>
                <input
                  v-model="filters.vip_only"
                  @change="loadCustomers"
                  type="checkbox"
                  class="filter-checkbox"
                />
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="loading-state">
            <div class="loading-spinner"></div>
            <p>Loading customers...</p>
          </div>

          <!-- Customers Table -->
          <div v-else class="table-container">
            <div class="table-wrapper">
              <table class="customers-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Country</th>
                    <th v-if="!isAgent">Category</th>
                    <th>Status</th>
                    <th v-if="!isAgent">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="customers.length === 0">
                    <td colspan="6" class="empty-state">
                      <div class="empty-content">
                        <span class="empty-icon">👥</span>
                        <p>No customers found</p>
                      </div>
                    </td>
                  </tr>
                  <tr v-for="customer in customers" :key="customer.id" class="customer-row">
                    <td>
                      <div class="customer-name">
                        {{ customer.full_name }}
                        <span v-if="customer.vip" class="vip-badge">VIP</span>
                      </div>
                    </td>
                    <td>{{ customer.phone_number }}</td>
                    <td>{{ customer.country || 'Unknown' }}</td>
                    <td v-if="!isAgent">
                      <select
                        :value="customer.category_id || ''"
                        @change="updateCustomerCategory(customer, $event.target.value)"
                        class="category-dropdown"
                      >
                        <option value="">No Category</option>
                        <option
                          v-for="category in categories"
                          :key="category.id"
                          :value="category.id"
                        >
                          {{ category.name }}
                        </option>
                      </select>
                    </td>
                    <td>
                      <span class="status-badge" :class="getStatusClass(customer)">
                        <svg v-if="customer.needs_agent" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style="margin-right: 4px;">
                          <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9H20.5L19 7.5V6C19 5.45 18.55 5 18 5H16C15.45 5 15 5.45 15 6V7.5L13.5 9H10.5L9 7.5V6C9 5.45 8.55 5 8 5H6C5.45 5 5 5.45 5 6V7.5L3.5 9H3C2.45 9 2 9.45 2 10V22C2 22.55 2.45 23 3 23H21C21.55 23 22 22.55 22 22V10C22 9.45 21.55 9 21 9Z"/>
                        </svg>
                        {{ getStatusText(customer) }}
                      </span>
                    </td>
                    <td v-if="!isAgent">
                      <div class="action-buttons">
                        <button @click="editCustomer(customer)" class="action-btn edit">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                          </svg>
                        </button>
                        <button @click="toggleVip(customer)" class="action-btn vip" :class="{ active: customer.vip }">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                          </svg>
                        </button>
                        <button @click="deleteCustomer(customer)" class="action-btn delete">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div v-if="pagination.total_pages > 1" class="pagination">
              <button
                @click="changePage(pagination.prev_page)"
                :disabled="!pagination.has_prev_page"
                class="pagination-btn"
              >
                Previous
              </button>
              <span class="pagination-info">
                Page {{ pagination.current_page }} of {{ pagination.total_pages }}
                ({{ pagination.total_records }} total)
              </span>
              <button
                @click="changePage(pagination.next_page)"
                :disabled="!pagination.has_next_page"
                class="pagination-btn"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>

    <!-- Success Snackbar -->
    <div v-if="showSuccessNotification" class="snackbar success-snackbar">
      <div class="snackbar-content">
        <span class="snackbar-icon">✅</span>
        <span class="snackbar-message">{{ successMessage }}</span>
      </div>
      <button @click="hideNotification" class="snackbar-close">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>
      </button>
    </div>

    <!-- Error Snackbar -->
    <div v-if="showErrorNotification" class="snackbar error-snackbar">
      <div class="snackbar-content">
        <span class="snackbar-icon">❌</span>
        <span class="snackbar-message">{{ errorMessage }}</span>
      </div>
      <button @click="hideNotification" class="snackbar-close">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>
      </button>
    </div>

    <!-- Upload Modal -->
    <div v-if="showUploadModal" class="modal-overlay" @click="closeUploadModal">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">Import Customers from Excel</h3>
          <button @click="closeUploadModal" class="modal-close">✖️</button>
        </div>
        <div class="modal-body">
          <div class="upload-instructions">
            <p><strong>Excel file requirements:</strong></p>
            <ul>
              <li>Required columns: <code>full_name</code>, <code>phone_number</code></li>
              <li>Optional columns: <code>address</code>, <code>category_id</code></li>
              <li>File formats: .xlsx or .xls</li>
              <li>First row should contain column headers</li>
            </ul>
          </div>
          <div class="file-upload-area">
            <input
              ref="fileInput"
              type="file"
              accept=".xlsx,.xls"
              @change="handleFileSelect"
              class="file-input"
            />
            <div class="upload-placeholder" @click="$refs.fileInput.click()">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" class="upload-icon">
                <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
              </svg>
              <p>Click to select Excel file or drag and drop</p>
              <p class="file-types">Supports .xlsx and .xls files</p>
            </div>
          </div>
          <div v-if="selectedFile" class="selected-file">
            <span>Selected: {{ selectedFile.name }}</span>
            <button @click="clearSelectedFile" class="clear-file-btn">✖️</button>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeUploadModal" class="btn-secondary">Cancel</button>
          <button @click="uploadFile" :disabled="!selectedFile || uploading" class="btn-primary">
            <span v-if="uploading" class="loading-spinner-small"></span>
            {{ uploading ? 'Uploading...' : 'Upload Customers' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Export Modal -->
    <div v-if="showExportModal" class="modal-overlay" @click="closeExportModal">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">Export Customers to Excel</h3>
          <button @click="closeExportModal" class="modal-close">✖️</button>
        </div>
        <div class="modal-body">
          <div class="export-options">
            <div class="form-group">
              <label class="form-label">Export Category</label>
              <select v-model="exportCategoryId" class="form-select">
                <option value="">All Categories</option>
                <option
                  v-for="category in categories"
                  :key="category.id"
                  :value="category.id"
                >
                  {{ category.name }}
                </option>
              </select>
            </div>
            <div class="export-info">
              <p>This will export all customers{{ exportCategoryId ? ' in the selected category' : '' }} to an Excel file.</p>
              <p>The file will include: Name, Phone, Country, Address, VIP Status, and Category.</p>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeExportModal" class="btn-secondary">Cancel</button>
          <button @click="exportCustomers" :disabled="exporting" class="btn-primary">
            <span v-if="exporting" class="loading-spinner-small"></span>
            {{ exporting ? 'Exporting...' : 'Export to Excel' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Edit Customer Modal -->
    <div v-if="showEditModal" class="modal-overlay" @click="closeEditModal">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">Edit Customer</h3>
          <button @click="closeEditModal" class="modal-close">✖️</button>
        </div>
        <div class="modal-body">
          <!-- Error Display -->
          <div v-if="editFormError" class="form-error">
            {{ editFormError }}
          </div>

          <div class="form-group">
            <label class="form-label">First Name *</label>
            <input v-model="editForm.first_name" type="text" class="form-input" placeholder="Enter first name" required>
          </div>
          <div class="form-group">
            <label class="form-label">Last Name *</label>
            <input v-model="editForm.last_name" type="text" class="form-input" placeholder="Enter last name" required>
          </div>
          <div class="form-group">
            <label class="form-label">Email</label>
            <input v-model="editForm.email" type="email" class="form-input" placeholder="Enter email address">
          </div>
          <div class="form-group">
            <label class="form-label">Phone Number</label>
            <input v-model="editForm.phone_number" type="tel" class="form-input" placeholder="Enter phone number">
          </div>
          <div class="form-group">
            <label class="form-label">Category</label>
            <select v-model="editForm.category_id" class="form-select">
              <option value="">Select Category</option>
              <option v-for="category in categories" :key="category.id" :value="category.id">
                {{ category.name }}
              </option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-checkbox-label">
              <input v-model="editForm.vip" type="checkbox" class="form-checkbox">
              VIP Customer
            </label>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeEditModal" class="btn-secondary">Cancel</button>
          <button @click="updateCustomer" :disabled="updating" class="btn-primary">
            <span v-if="updating" class="loading-spinner-small"></span>
            {{ updating ? 'Updating...' : 'Update Customer' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Add Customer Modal -->
    <div v-if="showAddModal" class="modal-overlay" @click="closeAddModal">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">Add New Customer</h3>
          <button @click="closeAddModal" class="modal-close">✖️</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label class="form-label">First Name *</label>
            <input v-model="addForm.first_name" type="text" class="form-input" placeholder="Enter first name" required>
          </div>
          <div class="form-group">
            <label class="form-label">Last Name *</label>
            <input v-model="addForm.last_name" type="text" class="form-input" placeholder="Enter last name" required>
          </div>
          <div class="form-group">
            <label class="form-label">Email *</label>
            <input v-model="addForm.email" type="email" class="form-input" placeholder="Enter email address" required>
          </div>
          <div class="form-group">
            <label class="form-label">Phone Number</label>
            <input v-model="addForm.phone_number" type="tel" class="form-input" placeholder="Enter phone number">
          </div>
          <div class="form-group">
            <label class="form-label">Category</label>
            <select v-model="addForm.category_id" class="form-select">
              <option value="">Select Category</option>
              <option v-for="category in categories" :key="category.id" :value="category.id">
                {{ category.name }}
              </option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-checkbox-label">
              <input v-model="addForm.vip" type="checkbox" class="form-checkbox">
              VIP Customer
            </label>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeAddModal" class="btn-secondary">Cancel</button>
          <button @click="addCustomer" :disabled="adding" class="btn-primary">
            <span v-if="adding" class="loading-spinner-small"></span>
            {{ adding ? 'Adding...' : 'Add Customer' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Sidebar from '../components/Sidebar.vue'
import TopHeader from '../components/TopHeader.vue'
import { DashboardService } from '../services/dashboard'

export default {
  name: 'CustomersView',
  components: {
    Sidebar,
    TopHeader
  },
  data () {
    return {
      isAgent: !!localStorage.getItem('agent_token'),
      loading: true,
      customers: [],
      categories: [],
      pagination: {},
      filters: {
        page: 1,
        per_page: 15,
        search_name: '',
        phone_number: '',
        category_id: '',
        vip_only: false
      },
      showAddModal: false,
      showEditModal: false,
      showUploadModal: false,
      showExportModal: false,
      uploading: false,
      exporting: false,
      showSuccessNotification: false,
      showErrorNotification: false,
      successMessage: '',
      errorMessage: '',
      notificationTimeout: null,
      searchTimeout: null,
      selectedFile: null,
      exportCategoryId: '',
      editForm: {
        id: null,
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        category_id: '',
        vip: false
      },
      addForm: {
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        category_id: '',
        vip: false
      },
      updating: false,
      adding: false,
      editFormError: ''
    }
  },
  async mounted () {
    await Promise.all([
      this.loadCustomers(),
      this.loadCategories()
    ])
  },
  methods: {
    async loadCustomers () {
      try {
        this.loading = true
        const result = await DashboardService.getCustomers(this.filters)

        if (result.success) {
          this.customers = result.customers
          this.pagination = result.pagination
        } else {
          console.error('Failed to load customers:', result.error)
          this.displayErrorNotification('Failed to load customers')
        }
      } catch (error) {
        console.error('Error loading customers:', error)
        this.displayErrorNotification('Error loading customers')
      } finally {
        this.loading = false
      }
    },

    async loadCategories () {
      try {
        const result = await DashboardService.getCustomerCategories()
        this.categories = result.categories || []
      } catch (error) {
        console.error('Error loading categories:', error)
        this.categories = []
      }
    },

    debouncedSearch () {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.filters.page = 1 // Reset to first page when searching
        this.loadCustomers()
      }, 500)
    },

    changePage (page) {
      if (page) {
        this.filters.page = page
        this.loadCustomers()
      }
    },

    getStatusClass (customer) {
      if (customer.case_resolved) return 'status-resolved'
      if (customer.needs_agent) return 'status-needs-agent'
      if (customer.assigned_to) return 'status-assigned'
      return 'status-unassigned'
    },

    getStatusText (customer) {
      if (customer.case_resolved) return 'Case Resolved'
      if (customer.needs_agent) return 'Needs Agent'
      if (customer.assigned_to) return 'Assigned'
      return 'Unassigned'
    },

    editCustomer (customer) {
      // Split full_name into first_name and last_name if needed
      const nameParts = customer.full_name ? customer.full_name.split(' ') : []
      const firstName = customer.first_name || nameParts[0] || ''
      const lastName = customer.last_name || (nameParts.length > 1 ? nameParts.slice(1).join(' ') : '')

      this.editForm = {
        id: customer.id,
        first_name: firstName,
        last_name: lastName,
        email: customer.email || '',
        phone_number: customer.phone_number || '',
        category_id: customer.category_id || '',
        vip: customer.vip === 1 || customer.vip === true
      }
      this.showEditModal = true
    },

    async toggleVip (customer) {
      try {
        const newVipStatus = customer.vip ? 0 : 1
        const result = await DashboardService.updateCustomerVip(customer.id, newVipStatus)

        if (result.success) {
          this.displaySuccessNotification(result.message)
          await this.loadCustomers()
        } else {
          this.displayErrorNotification(result.message)
        }
      } catch (error) {
        console.error('Error toggling VIP:', error)
        this.displayErrorNotification('Error updating VIP status')
      }
    },

    async updateCustomerCategory (customer, categoryId) {
      try {
        const result = await DashboardService.updateCustomerCategory(customer.id, categoryId || null)

        if (result.success) {
          this.displaySuccessNotification(result.message)
          await this.loadCustomers()
        } else {
          this.displayErrorNotification(result.message)
        }
      } catch (error) {
        console.error('Error updating category:', error)
        this.displayErrorNotification('Error updating customer category')
      }
    },

    async deleteCustomer (customer) {
      if (!confirm(`Are you sure you want to delete ${customer.full_name}?`)) return

      try {
        const result = await DashboardService.deleteCustomer(customer.id)

        if (result.success) {
          this.displaySuccessNotification(result.message)
          await this.loadCustomers()
        } else {
          this.displayErrorNotification(result.message)
        }
      } catch (error) {
        console.error('Error deleting customer:', error)
        this.displayErrorNotification('Error deleting customer')
      }
    },

    // Upload/Export Methods
    closeUploadModal () {
      this.showUploadModal = false
      this.selectedFile = null
    },

    closeExportModal () {
      this.showExportModal = false
      this.exportCategoryId = ''
    },

    closeEditModal () {
      this.showEditModal = false
      this.editForm = {
        id: null,
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        category_id: '',
        vip: false
      }
      this.updating = false
      this.editFormError = ''
    },

    closeAddModal () {
      this.showAddModal = false
      this.addForm = {
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        category_id: '',
        vip: false
      }
      this.adding = false
    },

    async updateCustomer () {
      // Clear any previous errors
      this.editFormError = ''

      console.log('Update customer called', this.editForm)
      console.log('Form validation check:')
      console.log('first_name:', this.editForm.first_name)
      console.log('last_name:', this.editForm.last_name)
      console.log('email:', this.editForm.email)

      if (!this.editForm.first_name || !this.editForm.last_name) {
        console.log('Validation failed - missing required fields')
        this.editFormError = 'Please fill in first name and last name'
        return
      }

      console.log('Validation passed, proceeding with API call')

      try {
        this.updating = true
        const customerData = {
          customer_id: this.editForm.id,
          full_name: `${this.editForm.first_name} ${this.editForm.last_name}`.trim(),
          phone_number: this.editForm.phone_number,
          category_id: this.editForm.category_id || null
        }

        // Only include VIP if it's true (1)
        if (this.editForm.vip) {
          customerData.vip = 1
        }

        // Only include email if it's not empty
        if (this.editForm.email && this.editForm.email.trim()) {
          customerData.email = this.editForm.email.trim()
        }

        console.log('Sending customer data:', customerData)
        console.log('Customer ID:', this.editForm.id)

        const result = await DashboardService.updateCustomer(this.editForm.id, customerData)

        console.log('Update result:', result)

        if (result.success) {
          this.displaySuccessNotification(result.message)
          this.closeEditModal()
          await this.loadCustomers()
        } else {
          this.editFormError = result.message || 'Failed to update customer'
        }
      } catch (error) {
        console.error('Error updating customer:', error)
        this.editFormError = 'Error updating customer: ' + error.message
      } finally {
        this.updating = false
      }
    },

    async addCustomer () {
      if (!this.addForm.first_name || !this.addForm.last_name) {
        this.displayErrorNotification('Please fill in first name and last name')
        return
      }

      try {
        this.adding = true
        const customerData = {
          full_name: `${this.addForm.first_name} ${this.addForm.last_name}`.trim(),
          phone_number: this.addForm.phone_number,
          category_id: this.addForm.category_id || null
        }

        // Only include VIP if it's true (1)
        if (this.addForm.vip) {
          customerData.vip = 1
        }

        // Only include email if it's not empty
        if (this.addForm.email && this.addForm.email.trim()) {
          customerData.email = this.addForm.email.trim()
        }

        const result = await DashboardService.addCustomer(customerData)

        if (result.success) {
          this.displaySuccessNotification(result.message)
          this.closeAddModal()
          await this.loadCustomers()
        } else {
          this.displayErrorNotification(result.message)
        }
      } catch (error) {
        console.error('Error adding customer:', error)
        this.displayErrorNotification('Error adding customer')
      } finally {
        this.adding = false
      }
    },

    handleFileSelect (event) {
      const file = event.target.files[0]
      if (file) {
        // Validate file type
        const allowedTypes = [
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'application/vnd.ms-excel'
        ]
        if (!allowedTypes.includes(file.type)) {
          this.displayErrorNotification('Please select a valid Excel file (.xlsx or .xls)')
          return
        }
        this.selectedFile = file
      }
    },

    clearSelectedFile () {
      this.selectedFile = null
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },

    async uploadFile () {
      if (!this.selectedFile) return

      try {
        this.uploading = true
        const result = await DashboardService.uploadCustomers(this.selectedFile)

        if (result.success) {
          const { results } = result
          const successCount = results.successful_imports || 0
          const errorCount = results.failed_imports || 0

          this.displaySuccessNotification(
            `Import completed! ${successCount} customers imported successfully.` +
            (errorCount > 0 ? ` ${errorCount} failed.` : '')
          )

          this.closeUploadModal()
          await this.loadCustomers()
        } else {
          this.displayErrorNotification(result.message || 'Failed to upload file')
        }
      } catch (error) {
        console.error('Error uploading file:', error)
        this.displayErrorNotification('Error uploading file')
      } finally {
        this.uploading = false
      }
    },

    async exportCustomers () {
      try {
        this.exporting = true
        const result = await DashboardService.exportCustomers(this.exportCategoryId || null)

        if (result.success && result.url) {
          // Open download link
          window.open(result.url, '_blank')
          this.displaySuccessNotification(
            `Export completed! ${result.totalCustomers} customers exported.`
          )
          this.closeExportModal()
        } else {
          this.displayErrorNotification(result.message || 'Failed to export customers')
        }
      } catch (error) {
        console.error('Error exporting customers:', error)
        this.displayErrorNotification('Error exporting customers')
      } finally {
        this.exporting = false
      }
    },

    displaySuccessNotification (message) {
      this.hideNotification()
      this.successMessage = message
      this.showSuccessNotification = true
      this.notificationTimeout = setTimeout(() => {
        this.hideNotification()
      }, 4000)
    },

    displayErrorNotification (message) {
      this.hideNotification()
      this.errorMessage = message
      this.showErrorNotification = true
      this.notificationTimeout = setTimeout(() => {
        this.hideNotification()
      }, 5000)
    },

    hideNotification () {
      this.showSuccessNotification = false
      this.showErrorNotification = false
      if (this.notificationTimeout) {
        clearTimeout(this.notificationTimeout)
        this.notificationTimeout = null
      }
    }
  },

  beforeUnmount () {
    if (this.notificationTimeout) {
      clearTimeout(this.notificationTimeout)
    }
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  }
}
</script>

<style scoped>
.customers-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
  transition: background-color 0.3s ease;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}

.page-content {
  flex: 1;
  padding: 32px;
}

.customers-container {
  max-width: 1400px;
  margin: 0 auto;
}

/* Page Header */
.page-header-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 32px;
}

.action-buttons-group {
  display: flex;
  gap: 12px;
}

.btn-primary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #3b82f6;
  color: white;
  border: 1px solid #3b82f6;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
  border-color: #2563eb;
}

.btn-icon {
  font-size: 16px;
}

/* Filters */
.filters-section {
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  padding: 24px;
  margin-bottom: 24px;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.filters-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr auto;
  gap: 16px;
  align-items: end;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filter-label {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  transition: color 0.3s ease;
}

.filter-input,
.filter-select {
  padding: 12px 16px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  font-size: 14px;
  color: var(--text-primary);
  background: var(--bg-primary);
  transition: all 0.2s;
}

.filter-input:focus,
.filter-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.filter-checkbox {
  width: 20px;
  height: 20px;
  margin-top: 8px;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 64px;
  color: #6b7280;
  text-align: center;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e5e7eb;
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Table */
.table-container {
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  overflow: hidden;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.table-wrapper {
  overflow-x: auto;
}

.customers-table {
  width: 100%;
  border-collapse: collapse;
}

.customers-table th {
  background: var(--bg-tertiary);
  padding: 16px 20px;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-secondary);
  border-bottom: 1px solid var(--border-primary);
  transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;
}

.customers-table td {
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-secondary);
  font-size: 14px;
  color: var(--text-primary);
  transition: color 0.3s ease, border-color 0.3s ease;
}

.customer-row:hover {
  background: var(--bg-tertiary);
  transition: background-color 0.2s ease;
}

.customer-name {
  display: flex;
  align-items: center;
  gap: 8px;
}

.vip-badge {
  background: #f59e0b;
  color: white;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: uppercase;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
}

.status-resolved {
  background: #d1fae5;
  color: #065f46;
}

.status-needs-agent {
  background: #fed7aa;
  color: #ea580c;
}

.status-assigned {
  background: #dbeafe;
  color: #1e40af;
}

.status-unassigned {
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  transition: background-color 0.3s ease, color 0.3s ease;
}

.category-dropdown {
  padding: 6px 12px;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  font-size: 13px;
  background: var(--bg-primary);
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.2s;
  min-width: 140px;
}

.category-dropdown:hover {
  border-color: #3b82f6;
}

.category-dropdown:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

.category-dropdown option {
  background: var(--bg-primary);
  color: var(--text-primary);
  padding: 8px 12px;
}

.action-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  color: #6b7280;
}

.action-btn:hover {
  background: #f3f4f6;
}

.action-btn.edit-btn:hover {
  color: #3b82f6;
}

.action-btn.vip-btn:hover {
  color: #f59e0b;
}

.action-btn.delete-btn:hover {
  color: #ef4444;
}

/* Pagination */
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-top: 1px solid var(--border-primary);
  background: var(--bg-tertiary);
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.pagination-info {
  font-size: 14px;
  color: var(--text-secondary);
  transition: color 0.3s ease;
}

.pagination-controls {
  display: flex;
  gap: 8px;
}

.pagination-btn {
  padding: 8px 12px;
  border: 1px solid var(--border-primary);
  background: var(--bg-primary);
  color: var(--text-primary);
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
  background: var(--bg-tertiary);
  border-color: var(--border-secondary);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-btn.active {
  background: #3b82f6;
  border-color: #3b82f6;
  color: white;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: var(--bg-primary);
  border-radius: 16px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  animation: modalSlideIn 0.3s ease-out;
  transition: background-color 0.3s ease;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 16px;
  border-bottom: 1px solid var(--border-primary);
  transition: border-color 0.3s ease;
}

.modal-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  transition: color 0.3s ease;
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  font-size: 24px;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;
}

.modal-close:hover {
  background: #f3f4f6;
  color: #374151;
}

.modal-body {
  padding: 24px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px 24px;
  border-top: 1px solid var(--border-primary);
  transition: border-color 0.3s ease;
}

/* Upload Modal Specific */
.upload-area {
  border: 2px dashed var(--border-primary);
  border-radius: 12px;
  padding: 32px 24px;
  text-align: center;
  background: var(--bg-tertiary);
  transition: all 0.2s;
  margin-bottom: 24px;
}

.upload-area.dragover {
  border-color: #3b82f6;
  background: #eff6ff;
}

.upload-icon {
  font-size: 48px;
  color: var(--text-secondary);
  margin-bottom: 16px;
  transition: color 0.3s ease;
}

.upload-text {
  margin-bottom: 8px;
  color: var(--text-primary);
  font-weight: 500;
  transition: color 0.3s ease;
}

.upload-hint {
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 16px;
  transition: color 0.3s ease;
}

.file-input {
  display: none;
}

.upload-button {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
}

.upload-button:hover {
  background: #2563eb;
}

.selected-file {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 16px;
}

.file-info {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #1e40af;
}

.remove-file {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;
}

.remove-file:hover {
  background: #f3f4f6;
  color: #374151;
}

/* Export Modal Specific */
.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 8px;
  transition: color 0.3s ease;
}

.form-select {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  font-size: 14px;
  color: var(--text-primary);
  background: var(--bg-primary);
  transition: all 0.2s;
}

.form-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-hint {
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 4px;
  transition: color 0.3s ease;
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  font-size: 14px;
  color: var(--text-primary);
  background: var(--bg-primary);
  transition: all 0.2s;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-checkbox {
  width: 18px;
  height: 18px;
  margin-right: 8px;
  accent-color: #3b82f6;
}

.form-checkbox-label {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: var(--text-primary);
  cursor: pointer;
  transition: color 0.3s ease;
}

.form-error {
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
  color: #991b1b;
  font-size: 14px;
  font-weight: 500;
}

/* Button Variants */
.btn-secondary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: var(--bg-primary);
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary:hover:not(:disabled) {
  background: var(--bg-tertiary);
  border-color: var(--border-secondary);
}

.btn-success {
  background: #10b981;
  border-color: #10b981;
}

.btn-success:hover:not(:disabled) {
  background: #059669;
  border-color: #059669;
}

.btn-import {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #10b981;
  color: white;
  border: 1px solid #10b981;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-import:hover:not(:disabled) {
  background: #059669;
  border-color: #059669;
}

.btn-export {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #3b82f6;
  color: white;
  border: 1px solid #3b82f6;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-export:hover:not(:disabled) {
  background: #2563eb;
  border-color: #2563eb;
}

.btn-primary:disabled,
.btn-secondary:disabled,
.btn-import:disabled,
.btn-export:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Loading Spinner */
.loading-spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Notifications */
.notification {
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 16px 20px;
  border-radius: 8px;
  box-shadow: 0 10px 25px -3px rgba(0, 0, 0, 0.1);
  font-size: 14px;
  font-weight: 500;
  z-index: 1100;
  animation: slideInRight 0.3s ease-out;
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.notification.success {
  background: #d1fae5;
  color: #065f46;
  border: 1px solid #a7f3d0;
}

.notification.error {
  background: #fee2e2;
  color: #991b1b;
  border: 1px solid #fecaca;
}

.empty-state {
  text-align: center;
  padding: 48px 20px;
}

.empty-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: var(--text-secondary);
  transition: color 0.3s ease;
}

.empty-icon {
  font-size: 48px;
  opacity: 0.5;
}

/* Upload Modal Specific */
.upload-instructions {
  background: var(--bg-tertiary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.upload-instructions ul {
  margin: 8px 0 0 0;
  padding-left: 20px;
}

.upload-instructions li {
  margin-bottom: 4px;
  font-size: 14px;
  color: var(--text-primary);
  transition: color 0.3s ease;
}

.upload-instructions code {
  background: var(--bg-secondary);
  color: var(--text-primary);
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  border: 1px solid var(--border-secondary);
  transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;
}

.upload-instructions p {
  color: var(--text-primary);
  margin-bottom: 8px;
  transition: color 0.3s ease;
}

.upload-instructions strong {
  color: var(--text-primary);
  transition: color 0.3s ease;
}

.file-upload-area {
  margin-bottom: 20px;
}

.upload-placeholder {
  border: 2px dashed var(--border-primary);
  border-radius: 12px;
  padding: 32px 24px;
  text-align: center;
  background: var(--bg-tertiary);
  cursor: pointer;
  transition: all 0.2s;
}

.upload-placeholder:hover {
  border-color: #3b82f6;
  background: #eff6ff;
}

.upload-placeholder p {
  margin: 0;
  color: #374151;
  font-size: 14px;
}

.file-types {
  color: #6b7280 !important;
  font-size: 12px !important;
  margin-top: 4px !important;
}

.selected-file {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 16px;
}

.selected-file span {
  color: #1e40af;
  font-size: 14px;
}

.clear-file-btn {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;
}

.clear-file-btn:hover {
  background: #f3f4f6;
  color: #374151;
}

/* Export Modal Specific */
.export-options {
  margin-bottom: 20px;
}

.export-info {
  background: var(--bg-secondary);
  border: 1px solid var(--border-secondary);
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.export-info p {
  margin: 0 0 8px 0;
  font-size: 14px;
  color: var(--text-secondary);
  transition: color 0.3s ease;
}

.export-info p:last-child {
  margin-bottom: 0;
}

/* Mobile and Responsive Styles */
@media (max-width: 1024px) {
  .main-content {
    margin-left: 0 !important;
  }

  .customers-table th:nth-child(3),
  .customers-table td:nth-child(3) {
    display: none; /* Hide Country column on tablets */
  }
}

@media (max-width: 768px) {
  .customers-layout {
    background: #f8fafc;
    overflow-x: hidden;
  }

  .main-content {
    margin-left: 0 !important;
    width: 100%;
    max-width: 100vw;
    min-height: 100vh;
    overflow-x: hidden;
  }
  .page-content {
    padding: 12px;
    padding-top: 76px;
    width: 100%;
    box-sizing: border-box;
  }

  .customers-container {
    max-width: 100%;
    width: 100%;
  }

  /* Mobile Header */
  .page-header-actions {
    margin-bottom: 16px;
  }

  .action-buttons-group {
    display: flex;
    gap: 8px;
    width: 100%;
  }

  .btn-import,
  .btn-export {
    flex: 1;
    padding: 10px 12px;
    font-size: 13px;
    justify-content: center;
  }

  .btn-primary {
    padding: 10px 16px;
    font-size: 13px;
    justify-content: center;
  }

  /* Mobile Filters */
  .filters-section {
    padding: 12px;
    margin-bottom: 12px;
    width: 100%;
    box-sizing: border-box;
  }

  .filters-row {
    display: flex !important;
    flex-direction: column;
    gap: 10px;
    width: 100%;
    grid-template-columns: none;
    align-items: stretch;
  }

  .filter-group {
    gap: 4px;
    width: 100%;
  }

  .filter-label {
    font-size: 13px;
    font-weight: 600;
  }

  .filter-input,
  .filter-select {
    padding: 10px 12px;
    font-size: 14px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    border: 1px solid #d1d5db;
    border-radius: 8px;
  }

  .filter-checkbox {
    width: 18px;
    height: 18px;
    margin-top: 6px;
  }

  /* Mobile Table - Only table scrolls horizontally */
  .table-container {
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    width: 100%;
    box-sizing: border-box;
  }

  .table-wrapper {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    width: 100%;
  }

  .customers-table {
    min-width: 700px; /* Table will scroll horizontally */
    width: auto;
  }

  .customers-table th,
  .customers-table td {
    padding: 10px 8px;
    font-size: 13px;
    white-space: nowrap;
  }

  .customers-table th:nth-child(1),
  .customers-table td:nth-child(1) {
    min-width: 150px; /* Name column */
  }

  .customers-table th:nth-child(2),
  .customers-table td:nth-child(2) {
    min-width: 130px; /* Phone number column */
  }

  .customers-table th:nth-child(3),
  .customers-table td:nth-child(3) {
    display: none; /* Hide Country column on mobile */
  }

  .customers-table th:nth-child(4),
  .customers-table td:nth-child(4) {
    min-width: 120px; /* Category column */
  }

  .customers-table th:nth-child(5),
  .customers-table td:nth-child(5) {
    min-width: 120px; /* Status column */
  }

  .customers-table th:nth-child(6),
  .customers-table td:nth-child(6) {
    min-width: 100px; /* Actions column */
  }

  /* Mobile Customer Name */
  .customer-name {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .vip-badge {
    font-size: 9px;
    padding: 2px 4px;
  }

  /* Mobile Category Dropdown */
  .category-dropdown {
    padding: 4px 8px;
    font-size: 12px;
    min-width: 100px;
  }

  /* Mobile Status Badge */
  .status-badge {
    padding: 3px 6px;
    font-size: 11px;
    display: flex;
    align-items: center;
    gap: 3px;
    flex-wrap: wrap;
  }

  .status-badge svg {
    width: 12px;
    height: 12px;
  }

  /* Mobile Action Buttons */
  .action-buttons {
    gap: 4px;
  }

  .action-btn {
    width: 28px;
    height: 28px;
    padding: 4px;
  }

  .action-btn svg {
    width: 14px;
    height: 14px;
  }

  /* Mobile Pagination */
  .pagination-container {
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    text-align: center;
  }

  .pagination-controls {
    justify-content: center;
    flex-wrap: wrap;
  }

  .pagination-btn {
    padding: 8px 12px;
    font-size: 13px;
    min-width: 36px;
  }

  /* Mobile Modals */
  .modal {
    max-width: 95%;
    margin: 20px auto;
    max-height: 90vh;
  }

  .modal-header {
    padding: 16px 16px 12px;
  }

  .modal-title {
    font-size: 16px;
  }

  .modal-body {
    padding: 16px;
  }

  .modal-footer {
    padding: 12px 16px 16px;
    flex-direction: column;
    gap: 8px;
  }

  .modal-footer .btn-primary,
  .modal-footer .btn-secondary {
    width: 100%;
    justify-content: center;
  }

  /* Mobile Form Groups */
  .form-group {
    margin-bottom: 16px;
  }

  .form-label {
    font-size: 13px;
    margin-bottom: 6px;
  }

  .form-input,
  .form-select {
    padding: 10px 12px;
    font-size: 14px;
  }

  .form-error {
    padding: 10px 12px;
    font-size: 13px;
    margin-bottom: 16px;
  }

  /* Mobile Upload Area */
  .upload-placeholder {
    padding: 24px 16px;
  }

  .upload-placeholder p {
    font-size: 13px;
  }

  .file-types {
    font-size: 11px !important;
  }

  .upload-instructions {
    padding: 12px;
    margin-bottom: 16px;
  }

  .upload-instructions li {
    font-size: 13px;
  }

  /* Mobile Export Options */
  .export-options {
    margin-bottom: 16px;
  }

  .export-info {
    padding: 12px;
    margin-top: 12px;
  }

  .export-info p {
    font-size: 13px;
  }

  /* Mobile Notifications */
  .notification {
    top: 10px;
    right: 10px;
    left: 10px;
    max-width: none;
    padding: 12px 16px;
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .page-content {
    padding: 8px;
  }

  /* Extra small screens - stack buttons vertically */
  .action-buttons-group {
    flex-direction: column;
    gap: 6px;
  }

  .btn-import,
  .btn-export {
    flex: none;
    width: 100%;
  }

  .customers-table {
    min-width: 600px; /* Still allow horizontal scroll */
  }

  .customers-table th,
  .customers-table td {
    padding: 8px 6px;
    font-size: 12px;
  }

  .customer-name {
    max-width: 120px;
    word-break: break-word;
  }

  .status-badge {
    font-size: 10px;
    padding: 2px 4px;
  }

  .action-btn {
    width: 28px;
    height: 28px;
  }

  .action-btn svg {
    width: 12px;
    height: 12px;
  }

  .pagination-controls {
    gap: 4px;
  }

  .pagination-btn {
    padding: 6px 8px;
    font-size: 12px;
    min-width: 32px;
  }
}

/* Landscape orientation adjustments */
@media (max-width: 768px) and (orientation: landscape) {
  .modal {
    max-height: 95vh;
  }

  .modal-body {
    max-height: 60vh;
    overflow-y: auto;
  }
}

/* Touch-friendly improvements */
@media (pointer: coarse) {
  .action-btn {
    min-width: 32px;
    min-height: 32px;
  }

  .category-dropdown {
    min-height: 32px;
  }

  .filter-input,
  .filter-select {
    min-height: 40px;
  }

  .btn-import,
  .btn-export,
  .btn-primary {
    min-height: 44px;
  }
}
</style>
